/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * NAIS deviations from the packaged `live-form-validation` (Nette + LiveForm).
 * No vendor copy/paste — each override saves the original and delegates to it
 * for everything it doesn't explicitly change.
 */
import { setChecked, setRadioChecked } from './native-checks';
import { installFormTabErrorHandling, syncFormTabErrors } from './form-tabs';

type NetteLike = {
    getValue: (elem: any) => unknown;
    toggle: (selector: string, visible: boolean, srcElement?: unknown) => void;
    initForm: (form: HTMLFormElement) => void;
    toggleForm: (form: HTMLFormElement, elem?: unknown) => void;
    validateControl?: (elem: HTMLElement) => boolean;
};

type LiveFormLike = {
    options: Record<string, any>;
    hasClass: (el: Element, className: string | false) => boolean;
    getMessageId: (el: HTMLElement) => string;
    getMessageElement: (el: HTMLElement) => HTMLElement;
    getMessageParent: (el: HTMLElement) => Element | null;
    setupHandlers: (el: HTMLElement) => void;
    processServerErrors: (el: HTMLElement) => void;
};

/** Entity picker autocomplete: text input with `parent` attribute resolves to a hidden input's value. */
function installGetValue(Nette: NetteLike): void {
    const originalGetValue = Nette.getValue;

    Nette.getValue = function naisGetValue(elem: any): unknown {
        if (elem && elem.tagName && typeof elem.getAttribute === 'function' && elem.getAttribute('parent') !== null) {
            const form = elem.closest('form');
            const parentId = elem.getAttribute('parent');
            const parentInput = form?.querySelector<HTMLInputElement>(`#${CSS.escape(parentId)}`);
            return parentInput ? parentInput.value : null;
        }

        return originalGetValue.call(Nette, elem);
    };
}

function getToggleTarget(selector: string): HTMLElement[] {
    let els: Element[];
    try {
        els = Array.from(document.querySelectorAll(selector));
    } catch {
        return [];
    }

    return els.map((el) => el.closest<HTMLElement>('.form-group, [data-nais-field]') ?? (el as HTMLElement));
}

function toggleHide(selector: string, visible: boolean): void {
    getToggleTarget(selector).forEach((el) => {
        el.hidden = !visible;
        el.style.display = visible ? '' : 'none';
    });
}

function toggleSlide(selector: string, visible: boolean): void {
    const $ = window.jQuery;
    getToggleTarget(selector).forEach((el) => {
        if ($) {
            visible ? $(el).slideDown() : $(el).slideUp();
        } else {
            el.hidden = !visible;
        }
    });
}

function toggleReadOnly(selector: string, visible: boolean): void {
    const $ = window.jQuery;
    getToggleTarget(selector).forEach((item) => {
        const $item = $ ? $(item) : null;

        if (visible) {
            $item?.find('.timepicker').each(function timepickerEach(this: HTMLElement) {
                $(this).datepicker('option', 'disabled', true);
                $(this).removeClass('timepicker').addClass('readonly_timepicker');
            });
            $item?.find('.datepicker').each(function datepickerEach(this: HTMLElement) {
                $(this).datepicker('option', 'disabled', true);
                $(this).removeClass('datepicker').addClass('readonly_datepicker');
            });
            $item?.find('.datetimepicker').each(function datetimePicker(this: HTMLElement) {
                $(this).datepicker('option', 'disabled', true);
                $(this).removeClass('datetimepicker').addClass('readonly_datetimepicker');
            });
            item.querySelectorAll<HTMLInputElement>('input').forEach((input) => {
                switch (input.type) {
                    case 'text':
                    case 'email':
                    case 'number':
                    case 'password':
                        input.removeAttribute('disabled');
                        input.setAttribute('readonly', 'readonly');
                        break;
                    case 'button':
                    case 'image':
                    case 'file':
                        input.disabled = true;
                        break;
                    case 'radio':
                    case 'checkbox':
                        input.disabled = true;
                        input.closest('.form-check')?.classList.add('disabled');
                        break;
                    default:
                        break;
                }
            });
            item.querySelectorAll('div.input-group-text').forEach((el) => el.classList.add('hide'));
            item.querySelectorAll('ul.dropdown-menu').forEach((el) => el.classList.add('hide'));
            item.querySelectorAll<HTMLTextAreaElement>('textarea').forEach((el) => { el.readOnly = true; });
            item.querySelectorAll('select').forEach((select) => {
                select.querySelectorAll('option:not(:checked)').forEach((option) => option.setAttribute('disabled', 'disabled'));
                select.parentElement?.querySelectorAll('.select2-selection__arrow').forEach((el) => el.classList.add('readonly-select-button'));
                select.parentElement?.querySelectorAll('.select2-selection__rendered').forEach((el) => el.classList.add('readonly-select'));
            });
        } else {
            $item?.find('.readonly_timepicker').each(function readonlyTimepickerEach(this: HTMLElement) {
                $(this).datepicker('option', 'disabled', false);
                $(this).removeClass('readonly_timepicker').addClass('timepicker');
            });
            $item?.find('.readonly_datepicker').each(function readonlyDatepickerEach(this: HTMLElement) {
                $(this).datepicker('option', 'disabled', false);
                $(this).removeClass('readonly_datepicker').addClass('datepicker');
            });
            $item?.find('.readonly_datetimepicker').each(function readonlyDatetimePickerEach(this: HTMLElement) {
                $(this).datepicker('option', 'disabled', false);
                $(this).removeClass('readonly_datetimepicker').addClass('datetimepicker');
            });
            item.querySelectorAll<HTMLInputElement>('input').forEach((input) => {
                switch (input.type) {
                    case 'text':
                    case 'email':
                    case 'number':
                    case 'password':
                        input.removeAttribute('readonly');
                        break;
                    case 'button':
                    case 'image':
                    case 'file':
                        input.disabled = false;
                        break;
                    case 'radio':
                    case 'checkbox':
                        input.disabled = false;
                        input.closest('.form-check')?.classList.remove('disabled');
                        break;
                    default:
                        break;
                }
            });
            item.querySelectorAll('div.input-group-text').forEach((el) => el.classList.remove('hide'));
            item.querySelectorAll('ul.dropdown-menu').forEach((el) => el.classList.remove('hide'));
            item.querySelectorAll<HTMLTextAreaElement>('textarea').forEach((el) => { el.readOnly = false; });
            item.querySelectorAll('select').forEach((select) => {
                select.querySelectorAll('option').forEach((option) => option.removeAttribute('disabled'));
                select.parentElement?.querySelectorAll('.select2-selection__arrow').forEach((el) => el.classList.remove('readonly-select-button'));
                select.parentElement?.querySelectorAll('.select2-selection__rendered').forEach((el) => el.classList.remove('readonly-select'));
            });
        }
    });
}

/** Saves original `data-nette-rules` before forcing `:filled`, restores it when REQUIRE is switched off. */
function toggleRequire(selector: string, visible: boolean): void {
    getToggleTarget(selector).forEach((item) => {
        if (visible) {
            item.classList.add('required');
            item.querySelectorAll('label > span.required').forEach((span) => span.remove());
            const label = item.querySelector('label');
            if (label) {
                label.insertAdjacentHTML('beforeend', '<span class="required"> *</span>');
            }

            item.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>('input, textarea, select').forEach((el) => {
                if (el.dataset.naisOriginalNetteRules === undefined) {
                    el.dataset.naisHadNetteRules = el.hasAttribute('data-nette-rules') ? '1' : '0';
                    el.dataset.naisOriginalNetteRules = el.getAttribute('data-nette-rules') ?? '';
                }
                el.required = true;
                el.setAttribute('data-nette-rules', '[{"op":":filled","msg":"Toto pole je povinné."}]');
            });
        } else {
            item.classList.remove('required');
            item.querySelectorAll('label > span.required').forEach((span) => span.remove());

            item.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>('input, textarea, select').forEach((el) => {
                el.required = false;
                const original = el.dataset.naisOriginalNetteRules;
                if (el.dataset.naisHadNetteRules === '1') {
                    el.setAttribute('data-nette-rules', original ?? '');
                } else {
                    el.removeAttribute('data-nette-rules');
                }
            });
        }
    });
}

function isTruthyToggleValue(value: unknown): boolean {
    return value === true
        || value === 1
        || value === '1'
        || value === 'true'
        || value === 'on';
}

function toggleValue(selector: string, value: unknown): void {
    const $ = window.jQuery;

    getToggleTarget(selector).forEach((item) => {
        item.querySelectorAll<HTMLInputElement>('input').forEach((input) => {
            switch (input.type) {
                case 'text':
                case 'email':
                case 'number':
                case 'password':
                    input.value = String(value);
                    break;
                case 'radio':
                    if (input.value === value) {
                        setRadioChecked(input);
                    }
                    break;
                case 'checkbox':
                    setChecked(input, isTruthyToggleValue(value));
                    break;
                default:
                    break;
            }
        });
        item.querySelectorAll<HTMLTextAreaElement>('textarea').forEach((textarea) => { textarea.value = String(value); });
        item.querySelectorAll<HTMLSelectElement>('select').forEach((select) => {
            select.value = String(value);
            if ($) {
                $(select).trigger('change');
            } else {
                select.dispatchEvent(new Event('change', { bubbles: true }));
            }
        });
    });
}

/**
 * `selector|HIDE|SLIDE|READ_ONLY|REQUIRE|VALUE|ALERT` suffix syntax.
 * Default (no suffix) stays HIDE — renderer wraps fields in `data-nais-field`
 * and historically hid the whole field wrapper, not just the input.
 */
function installToggle(Nette: NetteLike): void {
    Nette.toggle = function naisToggle(selector: string, visible: boolean): void {
        if (/^\w[\w.:-]*$/.test(selector)) {
            selector = `#${selector}`;
        }

        const parts = selector.split('|');
        let action = 'HIDE';
        let target = selector;

        if (parts.length > 1) {
            [target, action] = parts;
        }

        switch (action) {
            case 'HIDE':
                toggleHide(target, visible);
                break;
            case 'SLIDE':
                toggleSlide(target, visible);
                break;
            case 'READ_ONLY':
                toggleReadOnly(target, visible);
                break;
            case 'REQUIRE':
                toggleRequire(target, visible);
                break;
            case 'VALUE':
                if (visible) {
                    toggleValue(target, parts[2]);
                }
                break;
            case 'ALERT':
                if (visible) {
                    window.alert(parts[2]);
                }
                break;
            default:
                break;
        }
    };
}

/** Double-submit protection — layered on top of the packaged validation-only submit listener. */
function installDoubleSubmitProtection(Nette: NetteLike): void {
    const originalInitForm = Nette.initForm;

    Nette.initForm = function naisInitForm(form: HTMLFormElement): void {
        originalInitForm.call(Nette, form);

        if (form.dataset.naisFormAdapterInitialized === '1') {
            return;
        }
        form.dataset.naisFormAdapterInitialized = '1';

        form.addEventListener('submit', (event) => {
            if (event.defaultPrevented) {
                return;
            }

            if (form.classList.contains('nais-doublesubmit-protection')) {
                event.preventDefault();
                event.stopPropagation();
                return;
            }

            form.classList.add('nais-doublesubmit-protection');
            const submits = Array.from(
                form.querySelectorAll<HTMLElement>('[type="submit"], button:not([type])'),
            );
            submits.forEach((el) => el.classList.add('disabled'));

            window.setTimeout(() => {
                form.classList.remove('nais-doublesubmit-protection');
                submits.forEach((el) => el.classList.remove('disabled'));
            }, 3000);
        });
    };
}

/** Tracks the clicked submitter even when the click lands on an icon/span inside the button. */
function installSubmitterTracking(): void {
    if (document.documentElement.dataset.naisSubmitterTrackingBound === '1') {
        return;
    }
    document.documentElement.dataset.naisSubmitterTrackingBound = '1';

    document.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }
        const submitter = target.closest<HTMLButtonElement | HTMLInputElement>(
            'button[type="submit"], input[type="submit"], input[type="image"]',
        );
        if (submitter?.form) {
            (submitter.form as HTMLFormElement & Record<string, unknown>)['nette-submittedBy'] = submitter;
        }
    }, true);
}

/** Re-run toggles after changes that aren't a plain `change` on the control itself. */
function installReToggleListeners(Nette: NetteLike): void {
    if (document.documentElement.dataset.naisReToggleBound === '1') {
        return;
    }
    document.documentElement.dataset.naisReToggleBound = '1';

    document.addEventListener('change', (event) => {
        const target = event.target;
        if (target instanceof HTMLInputElement || target instanceof HTMLSelectElement || target instanceof HTMLTextAreaElement) {
            if (target.form) {
                Nette.toggleForm(target.form, target);
            }
        }
    }, true);

    const $ = window.jQuery;
    if ($) {
        $(document).on(
            'select2:select select2:unselect select2:clear',
            'select',
            function onSelect2Change(this: HTMLSelectElement) {
                Nette.validateControl?.(this);
                if (this.form) {
                    Nette.toggleForm(this.form, this);
                }
            },
        );
        $(document).on('change', '.timepicker, .datepicker, .datetimepicker', function onPickerChange(this: HTMLInputElement) {
            if (this.form) {
                Nette.toggleForm(this.form, this);
            }
        });
    }
}

function collectForms(root: ParentNode | Document): HTMLFormElement[] {
    const forms = new Set<HTMLFormElement>();

    if (root instanceof HTMLFormElement) {
        forms.add(root);
    } else if (root instanceof HTMLElement) {
        const ownerForm = root.closest<HTMLFormElement>('form');
        if (ownerForm) {
            forms.add(ownerForm);
        }
    }

    root.querySelectorAll<HTMLFormElement>('form').forEach((form) => forms.add(form));

    return Array.from(forms);
}

function isSupportedValidationControl(el: HTMLElement): el is HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement {
    return el instanceof HTMLInputElement
        || el instanceof HTMLSelectElement
        || el instanceof HTMLTextAreaElement;
}

function isNonValidatableControl(el: HTMLElement): boolean {
    if (!isSupportedValidationControl(el)) {
        return true;
    }

    const input = el as HTMLInputElement;
    const type = (input.type || '').toLowerCase();

    return type === 'button'
        || type === 'submit'
        || type === 'reset'
        || type === 'image';
}

function hasDisabledLiveValidation(el: HTMLElement, LiveForm: LiveFormLike): boolean {
    const disabledClass = LiveForm.options.disableLiveValidationClass;

    return typeof disabledClass === 'string'
        && disabledClass !== ''
        && LiveForm.hasClass(el, disabledClass);
}

function hasClientValidationMetadata(el: HTMLElement): boolean {
    return el.hasAttribute('required')
        || el.hasAttribute('data-nette-rules')
        || el.hasAttribute('data-fake-required')
        || el.hasAttribute('data-fake-nette-rules');
}

function shouldSkipLiveFormWiring(el: HTMLElement, LiveForm: LiveFormLike): boolean {
    return hasDisabledLiveValidation(el, LiveForm) && !hasClientValidationMetadata(el);
}

function getNonValidatableMessageElement(): HTMLElement {
    const id = 'lfv-nonvalidatable-message';
    let messageEl = document.getElementById(id);

    if (!messageEl) {
        messageEl = document.createElement('span');
        messageEl.id = id;
        messageEl.style.display = 'none';
        document.body.appendChild(messageEl);
    }

    return messageEl;
}

/**
 * Re-init for fields inserted into an already initialized form.
 *
 * Normal Nette AJAX snippet replacements are handled by nette.ajax.js `forms`
 * extension. Call this helper only for dynamic controls added inside an existing
 * form, such as CMS block components, dynamic containers/tables or entity rows.
 *
 * The packaged `Nette.initForm` skips already-`noValidate` forms, so this helper
 * explicitly wires LiveForm handlers for newly added elements and re-evaluates
 * toggles for their owner form.
 */
function installInitNetteForms(Nette: NetteLike, LiveForm: LiveFormLike): void {
    window.initNetteForms = (root: ParentNode | Document = document) => {
        collectForms(root).forEach((form) => {
            const activateServerErrors = form.dataset.naisFormTabsInitialized !== 'true';
            Nette.initForm(form);
            for (let i = 0; i < form.elements.length; i++) {
                const el = form.elements[i] as HTMLElement;
                if (isNonValidatableControl(el) || shouldSkipLiveFormWiring(el, LiveForm)) {
                    continue;
                }
                LiveForm.setupHandlers(el);
                LiveForm.processServerErrors(el);
            }
            Nette.toggleForm(form);
            syncFormTabErrors(form, { activate: activateServerErrors });
        });
    };
}

/** Skip live-validation wiring for date/time pickers — keyup/blur handlers steal focus from the widget. */
function installSetupHandlersSkip(LiveForm: LiveFormLike): void {
    const original = LiveForm.setupHandlers;

    LiveForm.setupHandlers = function naisSetupHandlers(el: HTMLElement): void {
        if (
            !el
            || isNonValidatableControl(el)
            || shouldSkipLiveFormWiring(el, LiveForm)
            || LiveForm.hasClass(el, 'datepicker')
            || LiveForm.hasClass(el, 'datetimepicker')
            || LiveForm.hasClass(el, 'timepicker')
        ) {
            return;
        }
        original.call(LiveForm, el);
    };
}

/**
 * Packaged `getMessageId` does `el.form.elements[el.name].tagName` — throws when the
 * control is associated via the `form` attribute but `form`/`elements[name]` is missing.
 */
function installGetMessageIdGuard(LiveForm: LiveFormLike): void {
    LiveForm.getMessageId = function naisGetMessageId(el: HTMLElement): string {
        const postfix = String(LiveForm.options.messageIdPostfix ?? '_message');
        if (!el) {
            return `lfv-unknown${postfix}`;
        }

        const input = el as HTMLInputElement;
        const named = input.form && input.name ? input.form.elements.namedItem(input.name) : null;
        const hasTagName = Boolean(named && (named as HTMLElement).tagName);
        let tmp = `${el.id || ''}${postfix}`;

        if (input.name && (!el.id || !hasTagName)) {
            const name = input.name.replace(/\[\]$/, '');
            const formId = input.form?.id ? input.form.id : 'frm';
            tmp = `${formId}-${name}${postfix}`;
        }

        let id = tmp;
        let i = 0;
        while (document.getElementById(id)) {
            id = `${id}_${++i}`;
        }

        return id;
    };
}

/**
 * Packaged `processServerErrors` does `parentEl.getElementsByClassName` with no null check.
 * Header submit buttons (`form="…"` outside `<form>`) and detached associated controls
 * can have a null parent during snippet insert.
 */
function installProcessServerErrorsGuard(LiveForm: LiveFormLike): void {
    const original = LiveForm.processServerErrors;

    LiveForm.processServerErrors = function naisProcessServerErrors(el: HTMLElement): void {
        if (!el || typeof el.getAttribute !== 'function' || isNonValidatableControl(el)) {
            return;
        }
        if ('isConnected' in el && el.isConnected === false) {
            return;
        }
        if (!LiveForm.getMessageParent(el)) {
            return;
        }
        original.call(LiveForm, el);
    };
}

/**
 * Hides the (empty) message span for button/submit controls and datagrid filter fields,
 * and never appends a message node directly under `<tr>`/`<table>` — browsers invent an
 * anonymous cell there, which breaks the datagrid thead width.
 */
function installMessageElementOverrides(LiveForm: LiveFormLike): void {
    LiveForm.getMessageElement = function naisGetMessageElement(elArg: HTMLElement): HTMLElement {
        if (!elArg || typeof elArg.getAttribute !== 'function') {
            return getNonValidatableMessageElement();
        }

        let el = elArg;
        if (el.getAttribute('name')?.match(/\[\]$/)) {
            const form = (el as HTMLInputElement).form;
            const named = form?.elements.namedItem(el.getAttribute('name') as string);
            if (named && !(named as unknown as { tagName?: string }).tagName) {
                el = (named as unknown as HTMLElement[])[0] ?? el;
            } else if (named) {
                el = named as unknown as HTMLElement;
            }
        }

        if (isNonValidatableControl(el)) {
            return getNonValidatableMessageElement();
        }

        let id = el.getAttribute('data-lfv-message-id');
        if (!id) {
            id = LiveForm.getMessageId(el);
            el.setAttribute('data-lfv-message-id', id);
        }

        let messageEl = document.getElementById(id);
        if (!messageEl) {
            messageEl = document.createElement(LiveForm.options.messageTag);
            messageEl.id = id;

            const inputEl = el as HTMLInputElement;
            const isHiddenControl = inputEl.style.display === 'none' && !LiveForm.hasClass(el, LiveForm.options.enableHiddenMessageClass);
            const isNonValidatable = isNonValidatableControl(el) || (inputEl.name ?? '').indexOf('filter') > -1;
            if (isHiddenControl || isNonValidatable) {
                messageEl.style.display = 'none';
            }

            const parentEl = LiveForm.getMessageParent(el);
            if (parentEl?.classList.contains('input-group') && parentEl.parentNode) {
                // BS5 `.input-group` wraps the control — insert the message after the
                // group, not inside it (would break the input-group flex layout).
                const outer = parentEl.parentNode;
                if (parentEl.nextSibling) {
                    outer.insertBefore(messageEl, parentEl.nextSibling);
                } else {
                    outer.appendChild(messageEl);
                }
            } else if (parentEl === el.parentElement) {
                parentEl.insertBefore(messageEl, el.nextSibling);
            } else if (parentEl) {
                typeof parentEl.append === 'function' ? parentEl.append(messageEl) : parentEl.appendChild(messageEl);
            }
        }

        return messageEl;
    };

    LiveForm.getMessageParent = function naisGetMessageParent(el: HTMLElement): Element | null {
        let parentEl: Element | null = el.parentElement;
        let parentFound = false;

        if (LiveForm.options.messageParentClass !== false) {
            parentFound = true;
            while (parentEl && !LiveForm.hasClass(parentEl, LiveForm.options.messageParentClass)) {
                parentEl = parentEl.parentElement;
                if (parentEl === null) {
                    parentEl = el.parentElement;
                    parentFound = false;
                    break;
                }
            }
        }

        const name = el.getAttribute('name');
        const form = (el as HTMLInputElement).form;
        if (!parentFound && name && form) {
            const named = form.elements.namedItem(name);
            if (named && !(named as unknown as { tagName?: string }).tagName) {
                parentEl = parentEl?.parentElement ?? parentEl;
            }
        }

        // Never append message nodes as direct children of <tr>/<table> — browsers
        // invent an anonymous cell at the end of the row (breaks datagrid thead width).
        if (parentEl && (parentEl.tagName === 'TR' || parentEl.tagName === 'TABLE')) {
            parentEl = el.closest('td, th') ?? parentEl;
        }

        return parentEl;
    };
}

export function installNaisFormAdapter(Nette: NetteLike, LiveForm: LiveFormLike): void {
    installGetValue(Nette);
    installToggle(Nette);
    installDoubleSubmitProtection(Nette);
    installSubmitterTracking();
    installReToggleListeners(Nette);
    installInitNetteForms(Nette, LiveForm);
    installSetupHandlersSkip(LiveForm);
    installGetMessageIdGuard(LiveForm);
    installProcessServerErrorsGuard(LiveForm);
    installMessageElementOverrides(LiveForm);
    installFormTabErrorHandling(Nette, LiveForm);
}
